import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { ChartFactory, ChartType, CommonChartUtil, ScatterChartCustomAttributes } from '@ddt-web/chart';
import { BenchmarkOption, ColorsConfig } from '@ddt-web/config';
import { CellUtil, ComponentData, Expression, PortfolioDataUtil, TabData } from '@ddt-web/data-service';
import { DcrUpdateUtil } from '@ddt-web/generic-utils';
import { ApplicationStateStoreProxyService, PerformanceState } from '@ddt-web/shared-stores';
import { UrlService } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as get from 'lodash/get';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import * as isNumber from 'lodash/isNumber';
import * as merge from 'lodash/merge';

import { SeriesLabelIcon } from '../enums/series-label-icon.enum';
import { GroupConfiguration } from '../performance-overview/models/group-configuration.interface';
import { PerformanceUtil } from '../utils/performance.util';

declare const Highcharts;

@UntilDestroy()
@Component({
	selector: 'perf-historical-performance',
	templateUrl: './historical-performance.component.html',
	styleUrls: ['./historical-performance.component.scss']
})
export class HistoricalPerformanceComponent implements OnChanges, OnInit {
	@Input() public dataGross: ComponentData;
	@Input() public chartData: ComponentData;
	@Input() public dataNetCustom: ComponentData;
	@Input() public chartDataNetCustom: ComponentData;
	@Input() public shouldShowCommonPeriodInfo: boolean = true;
	@Input() public hideTabBarIfOneTab: boolean = false;
	@Input() public customBenchmark?: string;
	@Input() public customAttributes?: any = {};
	@Input() public customAdvisoryFee: number;
	@Input() public showChart: boolean = true;
	@Input() public showCustomAdvisoryFeeButton: boolean = true;
	@Input() public showSectionSeparator: boolean = true;
	@Input() public displayISOCurrency?: string;
	@Input() public tooltipText: string;
	@Input() public isCustomFeeEnabled: boolean = false;


	public scatterChartAttributes: ScatterChartCustomAttributes;
	public asOfDate: string;
	public isCommonPeriodModalVisible: boolean = false;
	public benchmarkOptions = BenchmarkOption;

	public shouldShowHistoricalPerformance: boolean = false;

	public partnerTooltipNameTemplate: string;
	public partnerSeriesPrefixes: Array<string>;
	public combinedData: Array<Array<TabData>>;
	public groupedLegendColumnConfiguration: Array<GroupConfiguration>;
	public groupedLegendColours;
	public chartColClass: string = 'col-24';
	public seriesIcon = SeriesLabelIcon;
	public hasBenchmarkPortfolio: boolean = false;
	public selectedBenchmarkForCaptureRatio: string;
	public tabIndex: number; // select the initial tab zero

	public currentTabs;
	public columnWidths: number[];
	public columnConfigIds: string[];
	private readonly validGrossTableTabIds = [
		'historical.returns.total',
		'historical.returns.one.year',
		'historical.returns.three.year',
		'historical.returns.five.year'
	];
	private readonly validCustomFeeTableTabIds = [
		'historical.net.custom.returns.total',
		'historical.net.custom.returns.one.year',
		'historical.net.custom.returns.three.year',
		'historical.net.custom.returns.five.year'
	];

	public ChartType = ChartType;

	constructor(
		public contentService: ContentService,
		public urlService: UrlService,
		public applicationStateStoreProxyService: ApplicationStateStoreProxyService
	) { }

	public ngOnInit(): void {
		this.getColumnConfigIds();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (this.dataGross.success) {
			this.hasBenchmarkPortfolio = !isEmpty(this.urlService.getAcBenchmark()) || PortfolioDataUtil.hasBenchmarkPortfolio(this.dataGross.portfolioData);
			this.applicationStateStoreProxyService.getPerformanceState()
				.pipe(untilDestroyed(this))
				.subscribe((state: PerformanceState) => {
					if (this.hasBenchmarkPortfolio) {
						this.selectedBenchmarkForCaptureRatio = state.selectedBenchmarkForCaptureRatio || BenchmarkOption.BENCHMARK;
					}
				});

			const groupedLegendColoursCopy = [...ColorsConfig.MULTI_SERIES_PORTFOLIO_COLORS];
			groupedLegendColoursCopy.pop();
			this.groupedLegendColours = groupedLegendColoursCopy;
			this.partnerTooltipNameTemplate = this.contentService.get('HIST_PERF_PARTNER_TOOLTIP_FORMAT', '<div><b>$portfolio$</b></div><div>$prefix$</div>');
			this.partnerSeriesPrefixes = [
				this.contentService.get('HIST_PERF_PARTNER_TOOLTIP_GROSS_PREFIX', 'Gross Performance'),
				this.contentService.get('HIST_PERF_PARTNER_TOOLTIP_CUSTOM_PREFIX', 'Net Performance')
			];
			this.groupedLegendColumnConfiguration = [
				{
					heading: this.contentService.get('GROUPED_LEGEND_CATEGORY_1_HEADING', 'Gross Performance'),
					subHeading: this.contentService.get('GROUPED_LEGEND_CATEGORY_1_SUBHEADING', null)
				},
				{
					heading: DcrUpdateUtil.getAdvisoryFeeTextForPerformanceLegend(
						this.contentService,
						'GROUPED_LEGEND_CATEGORY_3_HEADING',
						'Net of $ADVISORY_FEE$% advisory fee',
						this.customAdvisoryFee
					),
					subHeading: this.contentService.get('GROUPED_LEGEND_CATEGORY_3_SUBHEADING', '')
				}
			];

			this.asOfDate = get(this.dataGross.getTabById('hypo-perf-as-of-date'), 'tableData[0].cells[0][0].formattedValue');

			// check if tabs of historical gross performance are valid; if gross performance is not valid, tabs will be [] due to hideEmptyTab
			const isHistoricalGrossPerformanceValid = this.dataGross.tabs.filter(tab => get(tab, 'config.id') !== 'hypo-perf-as-of-date').length > 0;
			const isHistoricalNetCustomPerformanceValid = (this.dataNetCustom?.tabs ?? []).some(tab => CellUtil.isValidCellData(tab));

			this.shouldShowHistoricalPerformance = this.isCustomFeeEnabled && this.customAdvisoryFee ?
				isHistoricalGrossPerformanceValid && isHistoricalNetCustomPerformanceValid : isHistoricalGrossPerformanceValid;

			this.combinedData = this.createCombinedDataFromComponentData(this.chartData, this.isCustomFeeEnabled && this.customAdvisoryFee ? this.chartDataNetCustom : null);
			this.checkForChartDataChanges(changes?.chartData, this.combinedData);

			if (this.isCustomFeeEnabled) {
				this.chartColClass = 'col-18';
			}
		}

		// reset tabs on init & when custom fee is updated/ excluded
		this.onTabClick(0); // select the initial tab zero
	}

	public onBenchmarkOptionChange(benchmarkOption?: string): void {
		this.getColumnConfigIds();
		this.applicationStateStoreProxyService.updateSelectedBenchmarkForCaptureRatio({ selectedBenchmarkForCaptureRatio: this.selectedBenchmarkForCaptureRatio || benchmarkOption });
	}

	public getCellValueById(idToFind: string, tab: TabData): any {
		return CellUtil.findByExpression(tab, [new Expression(idToFind, 'tableData[0].cells[0]', 'formattedValue')]);
	}

	public onTabClick(tabIndex: number): void {
		if (isNumber(tabIndex)) {
			this.tabIndex = tabIndex;
			this.currentTabs = this.getDataTableTabs(tabIndex);
		}
	}

	/**
	 * We call this method in onTabClick padding in the current tab index from the output of bfa-tabs
	 * Also called in onInit to set the initial tab zero
	 * This is so that we can select the current tab across multiple componentData objects
	 * The tabs are the data per time period e.g. total, 1y, 3y, 5y
	 *
	 * @param tabIndex the index of the currently selected tab in the template
	 */
	private getDataTableTabs(tabIndex): Array<TabData> {
		const validTabIds = [...this.validGrossTableTabIds];

		if (this.isCustomFeeEnabled && !isNil(this.customAdvisoryFee)) {
			validTabIds.push(...this.validCustomFeeTableTabIds);
		}

		// select the correct tab from each componentData
		const tabs: TabData[] = [
			this.dataGross?.tabs?.[tabIndex],
			this.dataNetCustom?.tabs?.[tabIndex]
		]
			.filter(tab => tab)
			.filter(tab => validTabIds.includes(tab.config.id))
			.map(tab => {
				if (this.validCustomFeeTableTabIds.includes(tab.config.id)) {
					// add advisory fee to net performance type cell
					return PerformanceUtil.updateNetCustomPerformanceCells(tab, this.customAdvisoryFee);
				}
				return tab;
			});

		this.setColumnWidths(tabs);

		return tabs;
	}

	private getColumnConfigIds(): void {
		if (this.hasBenchmarkPortfolio) {
			this.columnConfigIds = this.selectedBenchmarkForCaptureRatio === BenchmarkOption.BENCHMARK ?
				[BenchmarkOption.SNP500.toLowerCase()] : // Remove snp500 columns, if dropdown value is Benchmark
				[BenchmarkOption.BENCHMARK.toLowerCase()]; // Remove benchmark columns, if dropdown value is SNP500
		}
	}

	private calculateChartAttributes(combinedChartData: Array<Array<TabData>>): ScatterChartCustomAttributes {
		if (!combinedChartData) { return { allSeriesData: [], axisYMin: 0 }; }

		const allSeriesData = this.getAllSeriesData(combinedChartData);
		const customAttributes = {
			...this.customAttributes,
			allSeriesData: allSeriesData,
			axisYMin: CommonChartUtil.findMinFromData(allSeriesData.filter(data => data))
		};

		if (this.isCustomFeeEnabled) {
			const categories = ChartFactory.getCategoriesForTabData(combinedChartData[0]?.[0]);
			merge(customAttributes, { chart: { spacingRight: 1 } });
			merge(customAttributes, { legend: { enabled: false } });
			merge(customAttributes, {
				tooltip: {
					formatter: function (): string {
						return (
							'<div class="performance-tooltip-container" style="width: 250px; background-color:rgba(255, 255, 255, 0.9);padding:10px;border:1px solid #a5a5a5">' +
							'<table width="100%">' +
							'<thead><tr><th colspan="2">' +
							this.series.name +
							'</th></tr></thead>' +
							'<tr class="spacer"></tr>' +
							'<tbody><tr>' +
							'<td style="text-align:left">' +
							categories[0] +
							'</td>' +
							'<td style="text-align:right"> ' +
							Highcharts.numberFormat(this.point.y, 2, '.', ',') +
							'%</td>' +
							'</tr>' +
							'<tr>' +
							'<td style="text-align:left">' +
							categories[1] +
							'</td>' +
							'<td style="text-align:right"> ' +
							Highcharts.numberFormat(this.point.x, 2, '.', ',') +
							'%</td>' +
							'</tr></tbody>' +
							'</table>' +
							'</div>'
						);
					}
				}
			});
			customAttributes.multiSeriesConfig = CommonChartUtil.createCustomisationsArray(
				this.groupedLegendColours,
				this.partnerSeriesPrefixes,
				this.partnerTooltipNameTemplate
			);
		}

		return customAttributes;
	}

	/**
	 * Checks:
	 * - if dataNet tabs are [], then it means we should not show historical net performance (tab is only created for partner users)
	 * - if dataNet tabs are not [], it checks if at least one tab is valid (has valid CellData cells)
	 *
	 * Returns true if:
	 * - historical net performance should not be shown (not a partner user)
	 * - we should show historical net performance and dataNet has at least one valid tab
	 *
	 * otherwise, false.
	 */
	private getAllSeriesData(combinedChartData: Array<Array<TabData>>): any[] {
		return combinedChartData.reduce((acc: any, chartData: Array<TabData>) => {
			acc.push(...ChartFactory.getDefaultScatterSeriesForTabData(chartData));
			return acc;
		}, []);
	}

	private checkForChartDataChanges(chartDataChanges: any, combinedChartData: Array<Array<TabData>>): void {
		if (chartDataChanges) {
			if (JSON.stringify(chartDataChanges.currentValue) !== JSON.stringify(chartDataChanges.previousValue)) {
				this.scatterChartAttributes = this.calculateChartAttributes(combinedChartData);
			}
		}
	}

	private createCombinedDataFromComponentData(...componentData: ComponentData[]): Array<Array<TabData>> {
		return componentData
			.filter((data: ComponentData) => data)
			.reduce((acc: Array<Array<TabData>>, componentData) => {
				componentData.tabs
					.filter((tab: TabData) => !tab.error)
					.forEach((tab: TabData, i: number) => {
						if (acc[i]) {
							acc[i] = [...acc[i], tab];
						} else {
							acc[i] = [tab];
						}
					});
				return acc;
			}, []);
	}

	private setColumnWidths(validTabs: Array<TabData>): void {
		// set column width for columns
		const visibleHeadersX = get(validTabs, '[0].tableData[0].headersX', []).filter(header => get(header, 'config.visible', true));
		const totalColumns = get(validTabs, '[0].tableData[0].firstColumnTitle') ? visibleHeadersX.length + 1 : visibleHeadersX.length;

		// set columnWidths based on total number of columns; this needs to sum up to 100
		if (totalColumns === 6) {
			this.columnWidths = [25, 15, 15, 15, 15, 15];
		} else if (totalColumns === 7) {
			this.columnWidths = [20, 20, 12, 12, 12, 12, 12];
		}
	}
}
